import React, { useState } from "react";
import apiClient from "../api/client";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Check, Zap, Crown, Star, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const SubscriptionModal = ({ isOpen, onClose, currentUsage }) => {
  const [isUpgrading, setIsUpgrading] = useState(false);
  const { toast } = useToast();

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    
    try {
      // Get current origin URL for success/cancel redirects
      const originUrl = window.location.origin;
      
      // Create Stripe checkout session
      const response = await apiClient.post('/subscription/checkout', {
        package_id: 'pro_monthly',
        origin_url: originUrl
      });

      if (response.data && response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received from server');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      
      let errorMessage = 'Failed to start checkout process. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast({
        title: "Payment Error",
        description: errorMessage,
        variant: "destructive"
      });
      
      setIsUpgrading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Upgrade to ItsMyVibe Pro
          </DialogTitle>
          <DialogDescription className="text-center text-lg">
            Unlock unlimited projects and AI improvements
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {/* Free Plan */}
          <Card className="relative">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">Free Plan</CardTitle>
                <Badge variant="secondary">Current</Badge>
              </div>
              <CardDescription>Perfect for getting started</CardDescription>
              <div className="text-3xl font-bold">$0<span className="text-sm font-normal">/month</span></div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">10 Projects</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">20 AI Improvements</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">End-to-end encryption</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Basic support</span>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <div className="text-sm font-medium mb-2">Your Usage:</div>
                <div className="text-xs space-y-1">
                  <div>Projects: {currentUsage?.projects_count || 0}/10</div>
                  <div>AI Improvements: {currentUsage?.ai_improvements_used || 0}/200</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card className="relative border-violet-200 shadow-lg">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-gradient-to-r from-violet-600 to-purple-600">
                <Crown className="w-3 h-3 mr-1" />
                Recommended
              </Badge>
            </div>
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                Pro Plan
                <Zap className="w-5 h-5 ml-2 text-violet-600" />
              </CardTitle>
              <CardDescription>For power users and professionals</CardDescription>
              <div className="text-3xl font-bold text-violet-600">
                $5<span className="text-sm font-normal text-slate-600">/month</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium">Unlimited Projects</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium">Unlimited AI Improvements</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">End-to-end encryption</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm">Priority support</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-4 h-4 text-violet-500" />
                  <span className="text-sm font-medium text-violet-700">Advanced AI models</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-4 h-4 text-violet-500" />
                  <span className="text-sm font-medium text-violet-700">Early access to new features</span>
                </div>
              </div>
              
              <Button 
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                onClick={handleUpgrade}
                disabled={isUpgrading}
              >
                {isUpgrading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirecting to Stripe...
                  </>
                ) : (
                  "Upgrade to Pro"
                )}
              </Button>
              
              <div className="text-center text-xs text-slate-500 mt-2">
                Cancel anytime • Secure payment via Stripe
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-6 text-center text-sm text-slate-600">
          <p>✨ Join thousands of users who have upgraded to Pro</p>
          <p className="mt-1">🔒 Secure payment processing powered by Stripe</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionModal;